import * as z from 'zod';

/** Why image generation is unavailable. Only meaningful when `canGenerateImage` is false. */
export const imageBlockedReasonSchema = z.enum(['NO_QUOTA', 'QUOTA_EXHAUSTED', 'FREE_CREDITS_EXHAUSTED']);

export const userPermissionsSchema = z.object({
  canGenerateVideo: z.boolean(),
  canManageVideoQuotas: z.boolean(),
  /**
   * Whether the user can generate an image right now. The backend owns this rule: it is the same
   * one that rejects the generation request, so the two can never drift apart.
   * Defaults to permissive, an older payload must never lock a user out.
   */
  canGenerateImage: z.boolean().default(true),
  /**
   * `catch` keeps an unknown reason or a malformed date from failing the whole profile:
   * the UI falls back to a generic message instead of leaving the user with no app at all.
   */
  imageBlockedReason: imageBlockedReasonSchema.nullable().catch(null),
  /** When the next generation becomes possible. Null when nothing unblocks on its own. */
  imageAvailableAt: z.iso.datetime().nullable().catch(null),
});

export const userRoleSchema = z.enum(['USER', 'STORE_MANAGER', 'BILLING_MANAGER', 'SUPER_ADMIN']);

export const userAnnuaireSchema = z.enum(['franchise', 'group']);

/**
 * Access model: which generation offer the user is on, discriminated by `type`.
 * Exactly one shape is present at a time (mutually exclusive).
 */
export const userAccessSchema = z.discriminatedUnion('type', [
  // Virtual store: current-month consumption + spent budget (TTC, euros).
  z.object({
    type: z.literal('USAGE_BASED'),
    currentMonth: z.object({
      images: z.number().int().nonnegative(),
      videos: z.number().int().nonnegative(),
      amount: z.number().nonnegative(),
    }),
  }),
  // Physical store, subscribed: capped by a rolling weekly quota this payload does not carry.
  // Whether generation is still allowed comes from `permissions.canGenerateImage`.
  z.object({
    type: z.literal('SUBSCRIPTION'),
  }),
  // Physical store, no subscription: remaining free image credits.
  z.object({
    type: z.literal('FREE_TIER'),
    freeImages: z.object({
      remaining: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
    }),
  }),
]);

export const userApiSchema = z.object({
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: userRoleSchema,
  annuaire: userAnnuaireSchema,
  permissions: userPermissionsSchema,
  access: userAccessSchema,
  isBusinessNumberRequired: z.boolean().default(false),
});

export type UserRole = z.infer<typeof userRoleSchema>;
export type UserAnnuaire = z.infer<typeof userAnnuaireSchema>;
export type UserPermissions = z.infer<typeof userPermissionsSchema>;
export type ImageBlockedReason = z.infer<typeof imageBlockedReasonSchema>;
export type UserAccess = z.infer<typeof userAccessSchema>;
export type UserAccessType = UserAccess['type'];
export type UserApi = z.infer<typeof userApiSchema>;
